import type React from "react";
import { twMerge } from "tailwind-merge";
import { ClipboardDocumentIcon } from "@heroicons/react/20/solid";
import { useCopyToClipboard } from "usehooks-ts";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

type WalletNumberProps = {
  label?: string;
  copy?: boolean;
  wallet: string | null;
  onSetWallet?: () => void;
} & React.PropsWithChildren<React.ComponentPropsWithoutRef<"div">>;

export const WalletNumber = ({
  label,
  wallet,
  onSetWallet = () => {},
  copy = false,
  children,
  ...props
}: WalletNumberProps) => {
  const { t } = useTranslation(["wallet"]);
  const [_, copyToClipboard] = useCopyToClipboard();

  const noWallet = wallet === null;

  return (
    <label>
      {label && <p className={"mb-2 dark:text-white"}>{label}</p>}
      <div
        className={twMerge(
          "flex flex-row items-center justify-between rounded-sm bg-slate-100 px-4 py-3 font-semibold text-slate-800 dark:bg-slate-800 dark:text-white",
          noWallet && "flex cursor-pointer justify-center rounded-sm border bg-white py-4",
          props.className
        )}
        onClick={() => {
          if (!wallet) onSetWallet();
        }}
        {...props}
      >
        {typeof wallet === "string" && <p className={"mb-0"}>{wallet ?? "No wallet"}</p>}
        {noWallet && <p className={"mb-0 text-primary"}>{t("kypay.input.new_wallet")}</p>}
        {children}
        {copy && (
          <ClipboardDocumentIcon
            className={"h-6 cursor-pointer"}
            onClick={() =>
              copyToClipboard(wallet ?? "")
                .then(() => toast.success(t("error.success_clipboard")))
                .catch(() => toast.error(t("error.error_clipboard")))
            }
          />
        )}
      </div>
    </label>
  );
};
